// Thin client for the platform API. Requests go to the same origin, so the session cookie is
// sent automatically. Errors arrive as RFC 9457 problem details and are raised as ApiError.
// Types of the public REST API (/v1) are generated from the OpenAPI document (`pnpm gen:api`,
// checked for drift in CI); the app-private /auth endpoints keep hand-written types here.

import type { components } from "./api-schema";
import { hasMessage, t } from "./i18n";

type Schemas = components["schemas"];
export type Member = Schemas["Member"];
export type MemberPage = Schemas["MemberPage"];
export type ApiKey = Schemas["ApiKey"];
export type ApiKeyPage = Schemas["ApiKeyPage"];
export type NewApiKey = Schemas["NewApiKey"];
export type CreatedApiKey = Schemas["CreatedApiKey"];
export type Entitlements = Schemas["Entitlements"];
export type AuditPage = Schemas["AuditPage"];
export type ExportStatus = Schemas["ExportStatus"];

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    // Stable code for problems people see; translated as error.<code> (T-112).
    public code?: string,
  ) {
    super(detail);
  }
}

/// The text to show for a failed request: the translated message for a known problem code,
/// else the server's (English) detail, else a generic message.
export function problemText(error: unknown): string {
  if (!(error instanceof ApiError)) return t("common.error");
  const key = `error.${error.code}`;
  if (error.code && hasMessage(key)) return t(key);
  const text = error.detail;
  return text.charAt(0).toUpperCase() + text.slice(1) + (/[.!?]$/.test(text) ? "" : ".");
}

/// The company's security settings act on every screen (T-420): a member who must set up
/// two-step sign-in goes to My account, a sign-in older than the company allows ends, and a
/// company that requires single sign-on sends its people to sign in through it.
function companyRules(code: string | undefined, detail: string) {
  if (code === "sso_required") {
    // The company signs in with its identity provider (T-421): sign-in again that way, with
    // the company's own message.
    try {
      sessionStorage.setItem("f60.ssoRequired", detail);
    } catch {
      /* storage blocked: the sign-in page shows the standard text */
    }
    void fetch("/auth/sign-out", { method: "POST", credentials: "same-origin" }).finally(() => window.location.assign("/sign-in?sso=required"));
  } else if (code === "mfa_required" && window.location.pathname !== "/account") {
    window.location.assign("/account?mfa=required");
  } else if (code === "session_expired") {
    void fetch("/auth/sign-out", { method: "POST", credentials: "same-origin" }).finally(() => window.location.assign("/sign-in"));
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (response.status === 204) {
    return undefined as T;
  }
  const isJson = response.headers.get("content-type")?.includes("json");
  const data = isJson ? await response.json() : undefined;
  if (!response.ok) {
    const detail =
      (data && typeof data.detail === "string" && data.detail) || response.statusText || "request failed";
    const code = data && typeof data.code === "string" ? data.code : undefined;
    companyRules(code, detail);
    throw new ApiError(response.status, detail, code);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

export type Role =
  | "owner"
  | "admin"
  | "content_manager"
  | "creator"
  | "sender"
  | "reviewer"
  | "analyst"
  | "viewer_only";

export interface MyCompany {
  id: string;
  name: string;
  slug: string;
  role: Role;
}

export interface Me {
  user_id: string;
  email: string;
  display_name: string;
  locale: string;
  email_verified: boolean;
  mfa_enabled: boolean;
  recovery_codes_left: number;
  companies: MyCompany[];
  active_company_id: string | null;
  active_role: Role | null;
  /// The custom role in the active company, if any (T-207).
  custom_role?: string | null;
  /// Permission keys in the active company (T-207, US-006).
  permissions?: string[];
  /// Leads a team in the active company (manages those teams).
  leads_teams?: boolean;
  /// FRAME60 staff with access to the operator console (T-423).
  operator?: boolean;
  /// An operator's open support access to the active company (T-423).
  support?: { company: string; ticket: string; expires_at: string; write: boolean } | null;
}

export function isAdmin(role: Role | null | undefined): boolean {
  return role === "owner" || role === "admin";
}

/// Whether the member holds a permission (T-207). Falls back to the standard role while the
/// session predates permissions.
export function can(me: Me, permission: string): boolean {
  if (me.permissions) return me.permissions.includes(permission);
  return isAdmin(me.active_role);
}

/// Settings opens for anyone with an admin-level permission, and for team leads (Teams only).
export function seesSettings(me: Me): boolean {
  return (me.permissions ?? []).some((p) => p.startsWith("admin.")) || isAdmin(me.active_role) || !!me.leads_teams;
}
